/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { Alert, Button, INodeProps, NodeTag } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import { showAdditionalFields } from '../checkout-gift-card.props.autogenerated';

export interface IGetFormInput {
    errorMessage: string;
    giftCardNumber: string;
    giftCardPin: string;
    giftCardExp: string;
    inputRef?: React.RefObject<HTMLInputElement>;
    inputPinRef?: React.RefObject<HTMLInputElement>;
    inputExpRef?: React.RefObject<HTMLInputElement>;
    supportExternalGiftCard?: boolean;
    additionalFields?: showAdditionalFields;
    disableAddGiftCard?: boolean;
    resources: {
        applyGiftCardButton: string;
        giftCardFormLabel: string;
        giftCardNumberLabel: string;
        giftCardPinLabel: string;
        giftCardExpLabel: string;
        giftCardAlertLabel: string;
        giftCardPinPlaceholderText: string;
        giftCardExpPlaceholderText: string;
    };
    onEnterGiftCardNumber(giftCardNumber: string): void;
    onEnterGiftCardPin(giftCardPin: string): void;
    onEnterGiftCardExp(giftCardExp: string): void;
    onApplyGiftCard(): Promise<void>;
}

export interface IForm {
    formProps: INodeProps;
    label: React.ReactNode;
    inputPinLabel: React.ReactNode;
    inputExpLabel: React.ReactNode;
    alert: React.ReactNode;
    inputProps: INodeProps;
    inputNumProps: INodeProps;
    inputPinProps: INodeProps;
    inputExpProps: INodeProps;
    inputNumber: React.ReactNode;
    inputPin: React.ReactNode;
    inputExp: React.ReactNode;
    applyButton: React.ReactNode;
    supportExternalGiftCard: boolean | undefined;
    showGiftCardPinInput: boolean | undefined;
    showGiftCardExpInput: boolean | undefined;
    alertFieldLabel: React.ReactNode;
}

/**
 * On Apply Function.
 * @param onApplyGiftCard -On Apply Gift Card Function.
 * @returns Call of Apply Gift Card Function.
 */
const onApplyHandler = (onApplyGiftCard: () => Promise<void>) => async (event: React.SyntheticEvent): Promise<void> => {
    event.preventDefault();
    await onApplyGiftCard();
};

/**
 * On change number Function.
 * @param onEnterGiftCardNumber -On enter gift card number Function.
 * @returns Call of on enter gift card number Function.
 */
const onChangeNumberHandler = (onEnterGiftCardNumber: (giftCardNumber: string) => void) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = (event.target.value || '').replace(new RegExp('[<>]', 'gi'), '');
    onEnterGiftCardNumber(value);
};

/**
 * On Change pin event.
 * @param onEnterGiftCardPin -On enter gift card pin Function.
 * @returns Call of on enter gift card pin Function.
 */
const onChangePinHandler = (onEnterGiftCardPin: (giftCardPin: string) => void) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = (event.target.value || '').replace(new RegExp('[<>]', 'gi'), '');
    onEnterGiftCardPin(value);
};

/**
 * On Change Event.
 * @param onEnterGiftCardExp -On enter gift card function.
 * @returns Call of On enter gift card function.
 */
// Need to filter out the month and year
const onChangeExpHandler = (onEnterGiftCardExp: (giftCardExp: string) => void) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = (event.target.value || '').replace(new RegExp('[<>]', 'gi'), '');
    onEnterGiftCardExp(value);
};

export const getForm = (options: IGetFormInput): IForm => {
    const {
        errorMessage,
        inputRef,
        inputPinRef,
        inputExpRef,
        giftCardNumber,
        giftCardPin,
        giftCardExp,
        onEnterGiftCardNumber,
        onEnterGiftCardPin,
        onEnterGiftCardExp,
        onApplyGiftCard,
        supportExternalGiftCard,
        additionalFields,
        disableAddGiftCard,
        resources: { applyGiftCardButton, giftCardFormLabel, giftCardNumberLabel, giftCardPinLabel, giftCardExpLabel,
            giftCardAlertLabel, giftCardPinPlaceholderText, giftCardExpPlaceholderText }
    } = options;

    const formProps = {
        className: classnames('ms-checkout-gift-card__form', { 'is-invalid': !!errorMessage }),
        onSubmit: onApplyHandler(onApplyGiftCard),
        tag: 'form' as NodeTag
    };

    const label = (
        <label id='ms-checkout-gift-card__label' className='ms-checkout-gift-card__input-label'>
            {giftCardFormLabel}
        </label>
    );

    const inputPinLabel = (
        <label id='ms-checkout-gift-card__input-pin-label' className='ms-checkout-gift-card__input-pin-label'>
            {giftCardPinLabel}
        </label>
    );

    const inputExpLabel = (
        <label id='ms-checkout-gift-card__input-exp-label' className='ms-checkout-gift-card__input-exp-label'>
            {giftCardExpLabel}
        </label>
    );

    const alertFieldLabel = (
        <label id='ms-checkout-gift-card__input-alert' className='ms-checkout-gift-card__input-alert-label'>
            {giftCardAlertLabel}
        </label>
    );

    const alert = (
        <Alert
            tag='span'
            id='ms-checkout-gift-card__error'
            className='ms-checkout-gift-card__input-error'
            role='alert'
            assertive={false}
            fade={false}
            includeAlertClass={false}
            isOpen={!!errorMessage}
        >
            {errorMessage}
        </Alert>
    );

    const inputProps = {
        className: 'ms-checkout-gift-card__input-fields'
    };

    const inputNumProps = {
        className: 'ms-checkout-gift-card__input-num-fields'
    };

    const inputPinProps = {
        className: 'ms-checkout-gift-card__input-pin-fields'
    };

    const inputExpProps = {
        className: 'ms-checkout-gift-card__input-exp-fields'
    };

    const inputNumber = (
        <input
            ref={inputRef}
            type='text'
            className='ms-checkout-gift-card__input-text form-control'
            aria-label={giftCardNumberLabel}
            onChange={onChangeNumberHandler(onEnterGiftCardNumber)}
            value={giftCardNumber}
            aria-labelledby='ms-checkout-gift-card__label ms-checkout-gift-card__error'
        />
    );

    const inputPin = (
        <input
            ref={inputPinRef}
            type='text'
            className='ms-checkout-gift-card__input-pin-text form-contol'
            aria-label={giftCardPinLabel}
            placeholder={giftCardPinPlaceholderText}
            onChange={onChangePinHandler(onEnterGiftCardPin)}
            value={giftCardPin}
            aria-labelledby='ms-checkout-gift-card__label ms-checkout-gift-card__error'
        />
    );

    const inputExp = (
        <input
            ref={inputExpRef}
            type='text'
            className='ms-checkout-gift-card__input-exp-text form-contol'
            aria-label={giftCardExpLabel}
            placeholder={giftCardExpPlaceholderText}
            onChange={onChangeExpHandler(onEnterGiftCardExp)}
            value={giftCardExp}
            aria-labelledby='ms-checkout-gift-card__label ms-checkout-gift-card__error'
        />
    );

    const applyButton = (
        <Button
            className='ms-checkout-gift-card__btn-apply'
            onClick={onApplyHandler(onApplyGiftCard)}
            aria-label={applyGiftCardButton}
            disabled={disableAddGiftCard || !giftCardNumber}
        >
            {applyGiftCardButton}
        </Button>
    );

    let showGiftCardPinInput = false;
    let showGiftCardExpInput = false;

    switch (additionalFields) {
        case showAdditionalFields.pin:
            showGiftCardPinInput = true;
            break;
        case showAdditionalFields.expirationDate:
            showGiftCardExpInput = true;
            break;
        case showAdditionalFields.pinAndExpirationDate:
            showGiftCardPinInput = true;
            showGiftCardExpInput = true;
            break;
        default:
    }

    return {
        formProps,
        label,
        inputPinLabel,
        inputExpLabel,
        alert,
        inputProps,
        inputNumProps,
        inputPinProps,
        inputExpProps,
        inputNumber,
        inputPin,
        inputExp,
        applyButton,
        supportExternalGiftCard,
        showGiftCardPinInput,
        showGiftCardExpInput,
        alertFieldLabel
    };
};
